/**
 * Forme d'URL des récitals.
 *
 * # Pourquoi `/recitals` et plus `/live`
 *
 * Le menu dit « Récital » depuis le 2026-09-14, et l'écran ne montre plus
 * seulement ce qui se joue à l'instant : il porte aussi ce qu'on a gardé. Une
 * adresse qui disait « live » annonçait donc une chose et en ouvrait deux.
 * `/recitals` nomme la destination, comme `/library` et `/learn` nomment la
 * leur — anglais, pluriel, au même rang qu'elles.
 *
 * Le segment `recital/` du milieu est tombé avec : `/recitals/{id}` se lit
 * d'un trait, là où `/live/recital/{id}` bégayait.
 *
 * Les routes de l'API, elles, restent sous `/api/live/…` : ce sont des noms de
 * service, et les renommer aurait demandé de garder les deux pendant qu'un
 * front déployé continue d'appeler les anciennes — pour un mot que personne ne
 * lit.
 *
 * # Pourquoi pas `/studio/recitals`
 *
 * Même raison que la bibliothèque (`lib/library/library-routes.ts`) :
 * `app/(main)/layout.tsx` ne rend `children` que **hors** des routes studio, et
 * recouvre celles-ci d'un calque « tourne ton appareil » en portrait sur
 * téléphone. Une vue `/studio/*` n'aurait donc rien rendu du tout, et aurait
 * été masquée sur l'appareil et l'orientation où l'on découvre justement qu'il
 * se passe quelque chose en direct.
 *
 * # Ce module ne dit pas comment on *entre* dans la salle
 *
 * Entrer, c'est le studio, après la salle d'attente d'un récital — voir
 * `live-link.ts` et `recital-entry.ts`. `/recitals` est la page où l'on regarde
 * qui joue ; ce sont deux gestes différents.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/live/recital-routes.test.ts`.
 */

export const RECITALS_BASE_PATH = "/recitals";
export const RECITALS_MINE_PATH = "/recitals/mine";

/**
 * « Mes enregistrements » : ce qu'on a gardé, et non ce à quoi on a rendez-vous.
 *
 * Une page à elle, et pas une section de « Mes récitals » : on n'y vient pas
 * pour la même raison, on n'y fait pas les mêmes gestes — renommer, supprimer —
 * et un récital repris y tient deux lignes là qu'il n'en tient qu'une ici.
 */
export const RECITALS_RECORDINGS_PATH = "/recitals/recordings";

/**
 * « Mes publications » : ce que le monde peut voir de ce qu'on a joué.
 *
 * Une page à part de « Mes enregistrements », parce que les deux ne répondent
 * pas à la même question. Là-bas : « qu'est-ce que je garde ? », une ligne par
 * dépôt, qu'on renomme et qu'on supprime. Ici : « qu'est-ce qui est en
 * ligne ? », une ligne par **prise publiée** — c'est elle qui porte un lien —,
 * avec le lien à recopier et le geste qui referme. Un enregistrement de cinq
 * essais dont deux sont publiés fait deux lignes ici, et une là-bas.
 */
export const RECITALS_PUBLISHED_PATH = "/recitals/published";

/**
 * Chaque récital a sa page.
 *
 * Un récital s'attend : il y a un compte à rebours, des
 * inscrits, une date à partager des jours à l'avance — donc une adresse qu'on
 * peut envoyer, ouvrir sur un téléphone, et retrouver. La salle d'attente vit
 * hors studio pour la même raison que `/recitals` : elle doit se rendre là où
 * le studio est recouvert.
 */
export const RECITAL_BASE_PATH = "/recitals";

/** La page publique d'une prise vit hors de `/recitals`, sous la même coque. */
export const TAKES_BASE_PATH = "/takes";

/** L'ancre des récitals à venir, sur l'accueil. */
export const RECITALS_UPCOMING_ANCHOR = `${RECITALS_BASE_PATH}#upcoming`;

/** L'ancre des récitals en direct, sur l'accueil. */
export const RECITALS_NOW_ANCHOR = `${RECITALS_BASE_PATH}#live-now`;

/** La salle d'attente d'un récital. */
export function recitalPath(id: string): string {
  return `${RECITAL_BASE_PATH}/${encodeURIComponent(id)}`;
}

/**
 * La page publique d'une prise publiée : le lien qu'on colle sur un réseau.
 *
 * # Pourquoi une page, et pas l'adresse du studio
 *
 * Deux raisons, et la seconde suffirait. D'abord l'aperçu : une carte de réseau
 * social se fabrique en lisant le HTML servi, et `/studio` est une coquille
 * cliente — son titre serait « Virtuosa » pour toutes les prises du monde.
 * Ensuite le premier geste : un studio ne peut pas jouer un son avant qu'on ait
 * cliqué quelque part (règle des navigateurs), et cette page est l'endroit où
 * ce clic est naturel. Elle porte l'affiche, elle dit ce qu'on va voir, et son
 * bouton ouvre le studio sur cette prise-là.
 *
 * # L'identifiant, pas le rang (2026-09-25)
 *
 * Le lien était `/recitals/{récital}/takes/2`, et « 2 » disait à qui le
 * recevait combien d'essais il avait fallu. L'identifiant de la prise ne dit
 * rien, et suffit : l'API rend son récital (`/api/live/takes/{id}`).
 *
 * # Dans le chemin, pas en requête
 *
 * Next ne passe que les segments de chemin à `opengraph-image.tsx` : une prise
 * en `?take=…` n'aurait pas pu avoir son affiche à elle.
 */
export function takePath(takeId: string): string {
  return `${TAKES_BASE_PATH}/${encodeURIComponent(takeId)}`;
}

/**
 * L'identifiant de la prise dont c'est la page, `null` ailleurs.
 *
 * Le replay s'y prépare derrière la page, avant qu'on clique « Regarder »
 * (`replay-context.tsx`) : il lui faut la prise sans attendre le studio.
 */
export function takeIdFromPathname(pathname: string): string | null {
  if (!pathname.startsWith(`${TAKES_BASE_PATH}/`)) {
    return null;
  }

  const segment = pathname.slice(TAKES_BASE_PATH.length + 1);

  if (segment === "" || segment.includes("/")) {
    return null;
  }

  return decodeURIComponent(segment);
}

/**
 * Vrai dans la salle d'attente d'un récital, quel qu'il soit.
 *
 * Les deux pages fixes sous `/recitals` en sont exclues nommément : depuis que
 * le segment `recital/` est tombé, `/recitals/mine` a exactement la forme d'un
 * identifiant de récital, et rien dans le chemin ne les distingue. C'est le
 * prix de l'adresse courte, et il se paie ici, en un endroit.
 */
export function isRecitalPathname(pathname: string): boolean {
  if (!pathname.startsWith(`${RECITAL_BASE_PATH}/`)) {
    return false;
  }

  return !FIXED_PAGES.has(pathname);
}

/** Ce qui vit sous `/recitals` sans être un récital. */
const FIXED_PAGES = new Set<string>([
  RECITALS_MINE_PATH,
  RECITALS_RECORDINGS_PATH,
  RECITALS_PUBLISHED_PATH,
]);

/**
 * Vrai sur `/recitals` et tout ce qui vit dessous — et sur la page d'une prise,
 * qui en est une, sous une adresse plus courte.
 */
export function isRecitalsPathname(pathname: string): boolean {
  return (
    pathname === RECITALS_BASE_PATH ||
    pathname.startsWith(`${RECITALS_BASE_PATH}/`) ||
    pathname.startsWith(`${TAKES_BASE_PATH}/`)
  );
}
